/* Skrrt title tag.
   1. The word "Skrrt" in the product title shakes like a spray can,
   2. its letters drop away,
   3. and the artist's own stroke paths are painted in live, one by one along their length, following the
      stroke map (strokes 1-8, the dot, then the star), with a little overspray at the nozzle.
   The word stays in the page as text for search engines and screen readers. */
(function () {
  'use strict';
  if (customElements.get('ll-skrrt-tag')) return;

  const SPEED = 1.9;        // svg units per ms while painting
  const OVERLAP = 0.35;     // next stroke starts when this much of the current one is left
  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const wait = (ms) => new Promise((r) => setTimeout(r, ms));
  const easeInOut = (t) => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
  const rand = (a, b) => a + Math.random() * (b - a);
  const gauss = () => (Math.random() + Math.random() + Math.random() - 1.5) / 1.5;
  const NS = 'http://www.w3.org/2000/svg';

  class LlSkrrtTag extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;
      this.word = this.querySelector('.skrrt__word');
      this.svg = this.querySelector('.skrrt__tag');
      this.paths = Array.from(this.svg.querySelectorAll('.skrrt__strokes path'));
      this.dot = this.svg.querySelector('.skrrt__dot');
      this.dotR = parseFloat(this.dot.dataset.r || 15);
      this.dotX = parseFloat(this.dot.getAttribute('cx'));
      this.dotY = parseFloat(this.dot.getAttribute('cy'));
      this.spray = this.svg.querySelector('.skrrt__spray');
      const group = this.svg.querySelector('.skrrt__strokes');
      this.dotAt = parseInt(group.dataset.dotAt || this.paths.length, 10); // the dot is sprayed after this many strokes
      this.lengths = this.paths.map((p) => p.getTotalLength());

      // Split the word into letters so they can fall one by one.
      const text = this.word.textContent;
      this.word.setAttribute('aria-label', text);
      this.word.textContent = '';
      this.letters = Array.from(text).map((ch, n) => {
        const s = document.createElement('span');
        s.className = 'skrrt__letter';
        s.setAttribute('aria-hidden', 'true');
        s.textContent = ch;
        s.style.setProperty('--n', n);
        s.style.setProperty('--r', rand(-40, 40).toFixed(0) + 'deg');
        s.style.setProperty('--x', rand(-0.25, 0.25).toFixed(2) + 'em');
        this.word.appendChild(s);
        return s;
      });

      this.classList.add('is-ready');
      if (reduceMotion()) { this.finish(); return; }
      this.hideStrokes();

      const start = () => { if (!this._started) { this._started = true; this.play(); } };
      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver((es) => { if (es.some((e) => e.isIntersecting)) { io.disconnect(); start(); } }, { threshold: 0.6 });
        io.observe(this);
      } else start();

      if (this.hasAttribute('data-replay')) {
        this.addEventListener('mouseenter', () => { if (this.classList.contains('is-done') && !this._painting) this.repaint(); });
      }
    }

    hideStrokes() {
      this.paths.forEach((p, i) => { p.style.strokeDasharray = this.lengths[i] + 1; p.style.strokeDashoffset = this.lengths[i] + 1; });
      this.dot.setAttribute('r', 0);
    }

    async play() {
      // Fonts change the word's width, so measure once they're in.
      if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch (e) { /* ignore */ } }
      await wait(650);
      // Lock the width to the word, then let it ease to the tag's width as the letters go.
      const wordW = this.word.getBoundingClientRect().width;
      this.style.width = wordW + 'px';
      this.classList.add('is-shaking');
      await wait(620);
      this.classList.remove('is-shaking');
      this.classList.add('is-dropping');
      requestAnimationFrame(() => { this.style.width = ''; }); // CSS eases it to the tag's width
      await wait(380);
      this.classList.add('is-painting');
      await this.paint();
      this.finish();
    }

    async repaint() {
      this.classList.remove('is-done');
      this.classList.add('is-painting', 'is-repaint');
      this.hideStrokes();
      await wait(120);
      await this.paint();
      this.classList.remove('is-repaint');
      this.finish();
    }

    paint() {
      this._painting = true;
      return new Promise((resolve) => {
        // Schedule the strokes in order; each one starts a moment before the last one finishes.
        let t = 0, dotAt = null;
        const plan = this.paths.map((p, i) => {
          if (i === this.dotAt) { dotAt = t + 60; t += 300; }
          const dur = Math.max(110, this.lengths[i] / SPEED);
          const item = { i, start: t, dur };
          t += dur * (1 - OVERLAP) + rand(20, 60);
          return item;
        });
        if (dotAt === null) { dotAt = t + 60; t += 300; }
        const total = Math.max(t + 200, dotAt + 260);
        const t0 = performance.now();
        let lastSpray = 0;

        const frame = (now) => {
          const el = now - t0;
          plan.forEach(({ i, start, dur }) => {
            const raw = (el - start) / dur;
            if (raw <= 0) return;
            const k = Math.min(1, raw);
            const len = this.lengths[i];
            this.paths[i].style.strokeDashoffset = (len + 1) * (1 - easeInOut(k));
            // Overspray particles at the nozzle position.
            if (k < 1 && now - lastSpray > 16) {
              const pt = this.paths[i].getPointAtLength(len * easeInOut(k));
              this.puff(pt.x, pt.y, 3, 16);
            }
          });
          if (now - lastSpray > 16) lastSpray = now;
          if (el >= dotAt) {
            const k = Math.min(1, (el - dotAt) / 220);
            this.dot.setAttribute('r', (this.dotR * easeInOut(k)).toFixed(2));
            if (!this._dotPuffed) { this._dotPuffed = true; this.puff(this.dotX, this.dotY, 18, 22); }
          }
          if (el < total) requestAnimationFrame(frame);
          else { this._dotPuffed = false; this._painting = false; resolve(); }
        };
        requestAnimationFrame(frame);
      });
    }

    // A few soft paint particles that drift and fade.
    puff(x, y, count, spread) {
      for (let n = 0; n < count; n++) {
        const c = document.createElementNS(NS, 'circle');
        c.setAttribute('cx', (x + gauss() * spread).toFixed(1));
        c.setAttribute('cy', (y + gauss() * spread).toFixed(1));
        c.setAttribute('r', rand(1.2, 3.6).toFixed(1));
        c.style.setProperty('--dx', (gauss() * 10).toFixed(1) + 'px');
        c.style.setProperty('--dy', (gauss() * 10).toFixed(1) + 'px');
        this.spray.appendChild(c);
        setTimeout(() => c.remove(), 700);
      }
    }

    finish() {
      this.paths.forEach((p) => { p.style.strokeDashoffset = 0; });
      this.dot.setAttribute('r', this.dotR);
      this.style.width = '';
      this.classList.remove('is-shaking', 'is-dropping', 'is-painting');
      this.classList.add('is-done');
    }
  }

  customElements.define('ll-skrrt-tag', LlSkrrtTag);
})();